import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DOMAINS } from '../../../shared/constants/domains';
import { getDueCards, buildDailyDeck, DAILY_DECK_SIZE, GRADUATION_THRESHOLD } from '../engine/scheduler';
import { domainAccuracy, readinessScore, weakestDomain } from '../engine/stats';

function ReadinessRing({ value, size = 120, stroke = 8 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = value >= 70 ? '#2ECC71' : value >= 40 ? '#F39C12' : '#E74C3C';
  const glowClass = value >= 70 ? 'text-glow-green' : value >= 40 ? 'text-glow-yellow' : 'text-glow-red';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#374151" strokeWidth={stroke}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="progress-ring-circle"
          style={{ filter: `drop-shadow(0 0 6px ${color}60)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-2xl font-bold ${glowClass}`} style={{ color }}>
          {value}%
        </span>
        <span className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">Ready</span>
      </div>
    </div>
  );
}

export default function Dashboard({ cards, sessionHistory }) {
  const dueCards = useMemo(() => getDueCards(cards), [cards]);
  const dailyDeck = useMemo(() => buildDailyDeck(cards), [cards]);
  const accuracy = useMemo(() => domainAccuracy(cards), [cards]);
  const readiness = useMemo(() => readinessScore(cards), [cards]);
  const weakest = useMemo(() => weakestDomain(cards), [cards]);

  const deckSize = dailyDeck.active.length;
  const overflowSize = dailyDeck.waiting.length;

  const quickActions = [
    { to: '/flashcards/review?mode=cram', icon: '\u{1F4DA}', label: 'Cram Mode', gradient: 'from-blue-600/20 to-purple-600/20' },
    { to: '/flashcards/review?mode=weak', icon: '\u{1F3AF}', label: 'Weak Spots', gradient: 'from-orange-600/20 to-red-600/20' },
    { to: '/flashcards/edit', icon: '\u2795', label: 'Add Card', gradient: 'from-green-600/20 to-teal-600/20' },
    { to: '/flashcards/browse', icon: '\u{1F50D}', label: 'Browse All', gradient: 'from-indigo-600/20 to-blue-600/20' },
  ];

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto page-enter">
      {/* Hero section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-white mb-1 text-glow-blue">Flashcard Engine</h1>
        <p className="text-gray-400 text-sm">Spaced repetition for ENCOR fact retrieval</p>
      </div>

      {/* Start session CTA */}
      <div className="card-gradient p-8 mb-8 text-center">
        <div className="text-5xl font-bold text-white mb-1 text-glow-blue">{dueCards.length}</div>
        <p className="text-gray-400 mb-2">cards due today</p>
        {dueCards.length > 0 ? (
          <>
            <p className="text-sm text-gray-500 mb-5">
              Daily deck: {deckSize} cards active{overflowSize > 0 ? `, ${overflowSize} waiting to rotate in` : ''}
              {' \u00B7 '}get each right {GRADUATION_THRESHOLD}x to master
            </p>
            <Link
              to="/flashcards/review"
              className="inline-block px-10 py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl
                hover:from-blue-500 hover:to-blue-400 font-semibold text-lg
                glow-blue hover:glow-blue-lg transition-all duration-200 active:scale-95"
            >
              Start Daily Deck
            </Link>
          </>
        ) : (
          <p className="text-green-400 mt-2 text-glow-green">All caught up! Come back tomorrow.</p>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {quickActions.map(action => (
          <Link
            key={action.to}
            to={action.to}
            className={`bg-gradient-to-br ${action.gradient} border border-gray-700/50 rounded-xl p-4
              text-center hover-lift group transition-all duration-200`}
          >
            <div className="text-2xl mb-2 group-hover:scale-110 transition-transform duration-200">{action.icon}</div>
            <div className="text-sm font-medium text-gray-200">{action.label}</div>
          </Link>
        ))}
      </div>

      {/* Readiness + weakest area */}
      <div className="card-gradient p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Exam Readiness</h3>
            {weakest && (
              <p className="text-sm text-gray-500">
                Weakest area: <span className="font-medium" style={{ color: weakest.color }}>{weakest.name}</span>
                <br />
                <span className="text-xs">Focus more cards here</span>
              </p>
            )}
          </div>
          <ReadinessRing value={readiness} />
        </div>
      </div>

      {/* Domain breakdown */}
      <div className="card-gradient p-6 mb-6">
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Domain Breakdown</h3>
        <div className="space-y-3">
          {DOMAINS.map(domain => {
            const d = accuracy[domain.id];
            const pct = d.seen > 0 ? Math.round((d.correct / d.seen) * 100) : 0;
            const dueInDomain = dueCards.filter(c => c.domain === domain.id).length;

            return (
              <div key={domain.id} className="flex items-center gap-3 group">
                <div
                  className="w-3 h-3 rounded-full shrink-0 transition-shadow duration-200 group-hover:shadow-lg"
                  style={{ backgroundColor: domain.color, boxShadow: `0 0 0 0 ${domain.color}` }}
                />
                <span className="text-sm text-gray-300 w-36 truncate">{domain.name}</span>
                <div className="flex-1 bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-2.5 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: domain.color,
                      opacity: d.seen > 0 ? 1 : 0.3,
                      boxShadow: d.seen > 0 ? `0 0 8px ${domain.color}40` : 'none',
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-10 text-right font-mono">
                  {d.seen > 0 ? `${pct}%` : '\u2014'}
                </span>
                <span className="text-xs text-gray-500 w-12 text-right">
                  {d.total} cards
                </span>
                {dueInDomain > 0 && (
                  <span className="text-xs text-yellow-500 w-10 text-right font-medium">{dueInDomain} due</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Deck info */}
      <div className="flex items-center justify-between text-sm text-gray-500 px-1">
        <span>{cards.length} total cards</span>
        <Link to="/flashcards/deck" className="text-blue-400 hover:text-blue-300 transition-colors">
          Manage Deck \u2192
        </Link>
      </div>
    </div>
  );
}
